import React, { useEffect, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiPlus } from 'react-icons/fi';

const DEFAULT_PAGE_SIZE = 5;

const CollapsiblePaginatedPanel = ({
    title,
    items,
    pageSize = DEFAULT_PAGE_SIZE,
    countLabel = 'item',
    summaryDetail = '',
    addLabel = 'Add',
    onAdd,
    emptyText = 'No items yet.',
    children,
}) => {
    const [page, setPage] = useState(0);
    const [expanded, setExpanded] = useState(false);

    const totalPages = Math.max(1, Math.ceil(items.length / pageSize) || 1);
    const safePage = Math.min(page, totalPages - 1);
    const startIndex = safePage * pageSize;
    const visibleItems = items.slice(startIndex, startIndex + pageSize);
    const showPagination = items.length > pageSize;
    const showCollapsed = items.length > pageSize && !expanded;
    const countText = `${items.length} ${countLabel}${items.length === 1 ? '' : 's'}`;

    useEffect(() => {
        if (page > totalPages - 1) {
            setPage(Math.max(0, totalPages - 1));
        }
    }, [page, totalPages]);

    useEffect(() => {
        if (items.length > pageSize) {
            setPage(Math.ceil(items.length / pageSize) - 1);
            setExpanded(false);
        } else {
            setExpanded(true);
            setPage(0);
        }
    }, []);

    const handleAdd = () => {
        onAdd();
        setExpanded(true);
        setPage(Math.ceil((items.length + 1) / pageSize) - 1);
    };

    if (showCollapsed) {
        return (
            <div className="border border-gray-200 rounded-md p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gray-50">
                <div>
                    <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                        {countText}
                        {summaryDetail ? ` · ${summaryDetail}` : ''}
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        type="button"
                        onClick={handleAdd}
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 px-3 py-2 border border-blue-200 rounded-md bg-white"
                    >
                        <FiPlus className="w-4 h-4" />
                        {addLabel}
                    </button>
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-gray-900 px-3 py-2 border border-gray-300 rounded-md bg-white"
                    >
                        <FiChevronDown className="w-4 h-4" />
                        Expand
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="border border-gray-200 rounded-md flex flex-col min-h-0">
            <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-gray-100 shrink-0 bg-white">
                <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
                    {items.length > 0 && (
                        <span className="text-xs text-gray-500">({items.length})</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {items.length > pageSize && (
                        <button
                            type="button"
                            onClick={() => setExpanded(false)}
                            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
                        >
                            <FiChevronUp className="w-4 h-4" />
                            Collapse
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleAdd}
                        className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                        <FiPlus className="w-4 h-4" />
                        {addLabel}
                    </button>
                </div>
            </div>

            <div className="p-4 space-y-3">
                {items.length === 0 ? (
                    <p className="text-sm text-gray-500">{emptyText}</p>
                ) : (
                    children(visibleItems, startIndex)
                )}
            </div>

            {showPagination && (
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50 text-sm">
                    <span className="text-gray-600">
                        Showing {startIndex + 1}-{Math.min(startIndex + pageSize, items.length)} of {items.length}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            disabled={safePage === 0}
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-40 hover:bg-white"
                        >
                            Prev
                        </button>
                        <span className="text-gray-700 min-w-[5rem] text-center">
                            Page {safePage + 1} / {totalPages}
                        </span>
                        <button
                            type="button"
                            disabled={safePage >= totalPages - 1}
                            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                            className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-40 hover:bg-white"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CollapsiblePaginatedPanel;
